import { useEffect, useState } from 'react'
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getAllClients, getAllClientsForExport } from '../Redux/actions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { CircularProgress, Tooltip, IconButton, Chip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { green } from '@mui/material/colors';
import { ChevronRight } from '@mui/icons-material';
import { deleteClient } from '../Redux/actions';
import ConfirmDialog from './Common/ConfirmDialog';
import AlertDialog from './Common/AlertDialog';
import SearchBox from './Common/SearchBox';
import { updateParams } from '../utils';
import Pagination from '@mui/material/Pagination';
import { Can } from '../Context/Can';
import Filter from './Common/Filter';
import useBackActionDetection from '../utils/UseBackActionDetection';
import { paginationStyle, buttonStyles } from '../theme/formStyles';

export default function ClientListing() {
    const dispatch = useDispatch();
    let navigate = useNavigate();
    const location = useLocation();
    const [queryParams, setQueryParams] = useSearchParams();
    const existingParams = Object.fromEntries([...queryParams])
    const [filterOptions, setFilterOptions] = useState(existingParams);
    const isBackAction = useBackActionDetection();


    const [clientData, setClientData] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [alertMessage, setAlertMessage] = useState();
    const [msgType, setMsgType] = useState("success");
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [regions, setRegions] = useState(null);
    const [totalPages, setTotalPages] = useState(null)
    const [showLoader, setShowLoader] = useState(true);
    const [isSearchOrFilterApplied, setIsSearchOrFilterApplied] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(null);
    const [isExporting, setIsExporting] = useState(false);



    const goToRoutes = (routeApi, replace = true) => {
        navigate(routeApi, { replace });
    }

    const fetchData = () => {
        setShowLoader(true)
        const params = {
            ...existingParams,
            title: existingParams?.search,
            page: isSearchOrFilterApplied ? 1 : existingParams?.page,

        }
        dispatch(getAllClients(params)).then((res) => {
            if (res && res.status === 200) {
                const respData = res.data
                if (!respData) {
                    setShowLoader(false);
                    return;
                }
                setClientData(respData.data)
                setShowLoader(false)
                setTotalPages(respData.totalPages)
                setTotalCount(respData.totalItems);
                setCurrentPage(respData.currentPage)
                const uniqueRegions = new Set();
                respData.allData?.forEach((item) => uniqueRegions.add(item.region));
                const uniqueRegionsArray = Array.from(uniqueRegions).filter((region) => region !== null);;
                setRegions(uniqueRegionsArray)
            }
        })
    }
    useEffect(() => {
        if (existingParams?.page) {
            return
        }
        updateQueryParams({ page: 1 })
    }, [queryParams])

    useEffect(() => {
        setIsSearchOrFilterApplied(false);
        fetchData();
    }, [queryParams])

    const deleteClientById = () => {
        dispatch(deleteClient(selectedItem)).then((resp) => {
            if (resp && resp.status === 200) {
                const result = resp.data;
                if (!result.success) {
                    setAlertMessage(result.message);
                    setMsgType("fail");
                    return;
                }
                setShowConfirmDialog(false)
                setSelectedItem(null);
                setAlertMessage(result.message);
                setMsgType("success");
                fetchData();
            } else {
                setAlertMessage('Deletion failed');
                setMsgType("fail");
            }
        })
    }

    const triggerDelete = (client) => {
        setShowConfirmDialog(true);
        setSelectedItem(client);
    }

    const handleDialogClose = () => {
        setAlertMessage(null);
        navigate('/client-listing');
    };

    const updateQueryParams = (params) => {
        const searchParams = updateParams(existingParams, params)
        navigate({ search: searchParams.toString() }, { replace: true });
    }

    const handleSearch = (value) => {
        updateQueryParams({ search: value, page: 1 });
        setIsSearchOrFilterApplied(true);
    }

    const handlePageChange = (event, newPage) => {
        updateQueryParams({ page: newPage });
    };

    const itemsPerPage = 20;

    const handleExportClients = () => {
        setIsExporting(true);
        dispatch(getAllClientsForExport()).then((res) => {
            setIsExporting(false);
            if (res && res.status === 200 && res.data?.success) {
                const exportData = res.data.data;
                if (exportData && exportData.length > 0) {
                    const worksheet = XLSX.utils.json_to_sheet(exportData);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
                    
                    // Auto-size columns
                    const colWidths = Object.keys(exportData[0]).map(key => ({
                        wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length))
                    }));
                    worksheet['!cols'] = colWidths;
                    
                    const fileName = `Clients_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
                    XLSX.writeFile(workbook, fileName);
                } else {
                    setAlertMessage('No data available for export');
                    setMsgType('fail');
                }
            } else {
                setAlertMessage('Failed to export clients');
                setMsgType('fail');
            }
        }).catch(() => {
            setIsExporting(false);
            setAlertMessage('Failed to export clients');
            setMsgType('fail');
        });
    };

    return (
        <Grid>
            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                }}
            >

                <Grid container>
                    <Grid item container spacing={2} paddingY={2} paddingX={3}>
                        <Grid item md={6} container justifyContent="flex-start" alignItems="center">
                            <Tooltip title="Download All Clients">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleExportClients}
                                    disabled={isExporting}
                                    startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                                    sx={{
                                        height: '36px',
                                        minHeight: '36px',
                                        padding: '6px 12px',
                                        color: 'white',
                                        backgroundColor: '#008080',
                                        '&:hover': {
                                            borderColor: '#009999',
                                            backgroundColor: '#009999',
                                        },
                                    }}
                                >
                                    {isExporting ? 'Exporting...' : 'Export'}
                                </Button>
                            </Tooltip>
                            <div className='w3-hide'>
                                <Can do="manage" on="CreateClient">
                                    <Button variant="contained"
                                        onClick={() => { goToRoutes('/client/create') }}
                                        sx={buttonStyles.contained}
                                    >Create New Client</Button>
                                </Can>
                            </div>
                        </Grid>
                        <Grid item md={6} container justifyContent="flex-end" direction="row" alignItems="center">
                            <h6 style={{ paddingRight: '5px', fontSize: '15px', margin: 0 }}>Search By: </h6>
                            <SearchBox
                                value={queryParams.get('search') || ''}
                                placeholder='Client Name / Domain / Facility / PID'
                                search={handleSearch}
                                isBrowserBackAction={isBackAction}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Filter
                    filterArea="Clients"
                    regionsOption={regions}
                    showFilterDialog={showFilterDialog}
                    setShowFilterDialog={setShowFilterDialog}
                    updateQueryParams={updateQueryParams}
                    queryParams={queryParams}
                    setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                />

                <div className='w3-padding-large'>
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className='client-table-head'>Sl.No.</TableCell>
                                    <TableCell className='client-table-head'>Client Name</TableCell>
                                    <TableCell className='client-table-head'>Domain</TableCell> 
                                    <TableCell className='client-table-head'>Contact</TableCell>
                                    <TableCell className='client-table-head'>SPOC</TableCell>
                                    <TableCell className='client-table-head'>Address</TableCell>
                                    <TableCell className='client-table-head'></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody style={{ wordWrap: "break-word"}}>
                                {showLoader ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : clientData?.length > 0 ? clientData.map((item,index) => (
                                    <TableRow key={item?.id}
                                        className="client-table-row" sx={{ verticalAlign: 'top' }}
                                        onClick={() => { goToRoutes(`/client/${item?.id}/assets`, false) }}
                                    >
                                        <TableCell>{(currentPage - 1) * itemsPerPage + index + 1}</TableCell>
                                        <TableCell>{item?.name} <br /> <br />
                                            {item.ex_data?.zoho?.outstanding > 0 && <Chip style={{ backgroundColor: '#e68a00', color: '#ffffff',}} size="small" label={` ₹${item.ex_data?.zoho?.outstanding}`} />} <br /> <br />
                                            {(item.ex_data?.openDeskTickets === 0 || item.ex_data?.openDeskTickets > 0) && <Chip style={{ backgroundColor: '#008080', color: '#ffffff',}} size="small" label={` Open Desk Tickets: ${item.ex_data?.openDeskTickets}`} />}
                                        </TableCell>
                                        <TableCell>{item?.domain}</TableCell>
                                        <TableCell>
                                            {item?.primary_contact}
                                            <div style={{ fontSize: '10px', marginTop: '6px', color: 'grey' }}>
                                                {item.ex_data?.zoho?.contactList?.map(item => (
                                                    <div key={`client-name-${item?.name}`}>{item?.name} {item?.email ? ` - ${item?.email}` : ''}</div>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell>{item?.clientSpoc?.name}</TableCell>
                                        <TableCell>{item?.address}</TableCell>
                                        <TableCell align='right' style={{ width: '5%' }}>
                                            <Tooltip title="Details">
                                                <IconButton aria-label="edit" >
                                                    <ChevronRight className="cursor-pointer"
                                                        style={{ color: '#008080' }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )) : (<TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <h5 className='w3-text-grey'>
                                            No Data Found
                                        </h5>
                                    </TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
                {(clientData && (clientData.length > 0) && !showLoader ) && (
                    <Grid container item xs={12} style={{ display: 'flex', justifyContent: 'center', paddingRight: '24px', marginBottom: '30px' }}>
                        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <span style={{ fontFamily: '"Helvetica Neue"', fontSize: '13px', color: '#3d3d3d' }}>
                                {Math.min((parseInt(existingParams.page) - 1) * itemsPerPage + 1, totalCount)}-
                                {Math.min(parseInt(existingParams.page) * itemsPerPage, totalCount)} of {totalCount}
                            </span>
                        </Grid>
                        <Pagination
                            page={parseInt(existingParams?.page) || 1}
                            count={totalPages}
                            variant="outlined"
                            shape="rounded"
                            sx={paginationStyle}
                            onChange={handlePageChange}
                        />
                    </Grid>
                )}
            </Box>
            {alertMessage ? (
                <AlertDialog
                    handleClose={() => setAlertMessage(null)}
                    message={alertMessage}
                    type={msgType}
                    handleDialogClose={handleDialogClose}
                />) : null}
            {showConfirmDialog
                ? (
                    <ConfirmDialog
                        handleCancel={() => {
                            setSelectedItem({})
                            setShowConfirmDialog(false)
                        }}
                        handleOk={deleteClientById}
                        message={'Are you sure to delete this client?'}
                    />
                ) : null}
        </Grid>
    )
}